import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard, RoleGuard } from './core';
import { SelectivePreloadingStrategy } from './strategies/selective-preloading.strategy';
import {
  AdminLayoutComponent,
  PublicLandingPageLayoutComponent,
  PublicLayoutComponent,
  StudentLayoutComponent,
} from './shared';
import { StudentViewLayoutComponent } from './shared/layouts/student-view-layout/student-view-layout.component';

const routes: Routes = [
  //Student/Veteran Section Start
  {
    path: 'student',
    canActivate: [AuthGuard, RoleGuard],
    component: StudentLayoutComponent,
    children: [
      { path: '', redirectTo: 'profile', pathMatch: 'full' },
      {
        path: 'profile',
        loadChildren: () =>
          import('./features/student/student/profile/profile.module').then(
            (m) => m.StudentProfileModule,
          ),
      },
      {
        path: 'success',
        loadChildren: () =>
          import('./features/student/student/success/success.module').then(
            (m) => m.SuccessModule,
          ),
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/student/student/dashboard/dashboard.module').then(
            (m) => m.StudentDashboardModule,
          ),
      },
      {
        path: 'education',
        loadChildren: () =>
          import('./features/student/student/education/education.module').then(
            (m) => m.StudentEducationModule,
          ),
      },
      {
        path: 'employment',
        loadChildren: () =>
          import(
            './features/student/student/employment/employment.module'
          ).then((m) => m.StudentEmploymentModule),
      },
      {
        path: 'benefits',
        loadChildren: () =>
          import('./features/student/student/benefits/benefits.module').then(
            (m) => m.StudentBenefitsModule,
          ),
      },
      {
        path: 'discounts',
        loadChildren: () =>
          import('./features/student/student/discounts/discounts.module').then(
            (m) => m.StudentDiscountModule,
          ),
      },
      {
        path: 'inbox',
        loadChildren: () =>
          import('./features/student/student/inbox/inbox.module').then(
            (m) => m.StudentInboxModule,
          ),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./features/student/student/settings/settings.module').then(
            (m) => m.StudentSettingsModule,
          ),
      },
      // {
      //   path: 'my-reviews',
      //   loadChildren: () =>
      //     import('./features/student/student/my-reviews/my-reviews.module').then(
      //       (m) => m.MyReviewsModule,
      //     ),
      // },
    ],
  },
  {
    path: 'student-view/:studentId',
    component: StudentViewLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './features/student/student/student-view/student-view.module'
          ).then((m) => m.StudentViewModule),
      },
    ],
  },
  //Student/Veteran Section End
  //College Admin Section Start
  {
    path: 'college',
    canActivate: [AuthGuard, RoleGuard],
    component: AdminLayoutComponent,
    children: [
      { path: '', redirectTo: 'profile/overview', pathMatch: 'full' },
      { path: 'profile', redirectTo: 'profile/overview', pathMatch: 'full' },
      {
        path: 'reports/activity',
        loadChildren: () =>
          import(
            './features/admin/college-admin/reports/activity-report/activity-report.module'
          ).then((m) => m.ActivityReportModule),
      },
      {
        path: 'reports/school-data',
        loadChildren: () =>
          import(
            './features/admin/college-admin/reports/school-data/school-data.module'
          ).then((m) => m.SchoolDataModule),
      },
      {
        path: 'reports/campaigns',
        loadChildren: () =>
          import(
            './features/admin/college-admin/reports/campaigns/campaigns.module'
          ).then((m) => m.CampaignsModule),
      },
      {
        path: 'reports/campaigns/detail',
        loadChildren: () =>
          import(
            './features/admin/college-admin/reports/campaigns/campaigns.module'
          ).then((m) => m.CampaignsModule),
      },
      {
        path: 'reports/paid-vs-non-paid',
        loadChildren: () =>
          import(
            './features/admin/college-admin/reports/non-paid-v-campaigns/non-paid-v-campaigns.module'
          ).then((m) => m.NonPaidVCampaignsModule),
      },
      {
        path: 'reports/platform-data',
        loadChildren: () =>
          import(
            './features/admin/college-admin/reports/platform-data/platform-data.module'
          ).then((m) => m.PlatformDataModule),
      },

      {
        path: 'inbox',
        loadChildren: () =>
          import('./features/admin/college-admin/inbox/inbox.module').then(
            (m) => m.AdminInboxModule,
          ),
      },
      {
        path: 'profile/overview',
        loadChildren: () =>
          import(
            './features/admin/college-admin/profile/overview/overview.module'
          ).then((m) => m.AdminOverviewModule),
      },
      {
        path: 'profile/military',
        loadChildren: () =>
          import(
            './features/admin/college-admin/profile/military/military.module'
          ).then((m) => m.AdminMilitaryModule),
      },
      {
        path: 'profile/yellow-ribbon',
        loadChildren: () =>
          import(
            './features/admin/college-admin/profile/yellow-ribbon-program/yellow-ribbon-program.module'
          ).then((m) => m.AdminYellowRibbonProgramModule),
      },
      {
        path: 'profile/stats',
        loadChildren: () =>
          import(
            './features/admin/college-admin/profile/stats/stats.module'
          ).then((m) => m.AdminStatsModule),
      },
      {
        path: 'profile/degrees',
        loadChildren: () =>
          import(
            './features/admin/college-admin/profile/degrees/degrees.module'
          ).then((m) => m.AdminDegreesModule),
      },
      {
        path: 'profile/contact',
        loadChildren: () =>
          import(
            './features/admin/college-admin/profile/contact/contact.module'
          ).then((m) => m.AdminContactModule),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import(
            './features/admin/college-admin/settings/settings.module'
          ).then((m) => m.AdminSettingsModule),
      },
    ],
  },
  //College Admin Section End

  // LPS Landing Pages - Must come before PublicLayoutComponent to avoid :college_alias conflict
  {
    path: 'lps',
    component: PublicLandingPageLayoutComponent,
    children: [
      {
        path: ':alias',
        loadChildren: () =>
          import('./features/general/lps/lps.module').then((m) => m.LPSModule),
      },
    ],
  },

  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      {
        path: 'styleguides',
        loadChildren: () =>
          import('./features/general/styleguide/styleguide.module').then(
            (m) => m.StyleguideModule,
          ),
      },
      {
        path: 'login',
        loadChildren: () =>
          import('./features/auth/login/login.module').then(
            (m) => m.LoginModule,
          ),
      },
      {
        path: 'register',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/pay-calculator',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/gi-bill-calculator',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/va-disability',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/active-duty-education-guide',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/spouse-education-guide',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/veteran-education-guide',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/bah',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/request-info/:college',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'register/contact-info/:college',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },
      {
        path: 'media',
        loadChildren: () =>
          import('./features/auth/register/register.module').then(
            (m) => m.RegisterModule,
          ),
      },

      {
        path: 'forgot-password',
        loadChildren: () =>
          import('./features/auth/forgot-password/forgot-password.module').then(
            (m) => m.ForgotPasswordModule,
          ),
      },
      {
        path: 'sso/update/password',
        loadChildren: () =>
          import('./features/auth/forgot-password/forgot-password.module').then(
            (m) => m.ForgotPasswordModule,
          ),
      },
      {
        path: 'term-of-use',
        loadChildren: () =>
          import('./features/general/term/term.module').then(
            (m) => m.TermsModule,
          ),
      },
      {
        path: 'security',
        loadChildren: () =>
          import('./features/general/security/security.module').then(
            (m) => m.SecurityModule,
          ),
      },
      {
        path: 'privacy',
        loadChildren: () =>
          import('./features/general/privacy/privacy.module').then(
            (m) => m.PrivacyModule,
          ),
      },
      {
        path: 'sitemap',
        loadChildren: () =>
          import('./features/general/sitemap/sitemap.module').then(
            (m) => m.SitemapModule,
          ),
      },
      {
        path: 'credit-estimator',
        loadChildren: () =>
          import(
            './features/calculators/credit-estimator/credit-estimator.module'
          ).then((m) => m.CreditEstimatorModule),
      },
      {
        path: 'gibill-calculator',
        loadChildren: () =>
          import(
            './features/calculators/gi-bill-calculator/gi-bill.module'
          ).then((m) => m.GiBillModule),
      },
      {
        path: 'student-loan-calculator',
        // component: StudentLoanCalculatorComponent,
        loadChildren: () =>
          import(
            './features/calculators/student-loan-calculator/student-loan-calculator.module'
          ).then((m) => m.StudentLoanCalculatorModule),
      },
      // {
      //   path: 'tcpa',
      //   component: TCPAComponent,
      // },
      {
        path: 'bah-calculator',
        // component: BahCalculatorComponent,
        loadChildren: () =>
          import(
            './features/calculators/bah-calculator/bah-calculator-route.module'
          ).then((m) => m.BahCalculatorRouteModule),
      },
      {
        path: 'insurance',
        // component: InsuranceComponent,
        loadChildren: () =>
          import('./features/insurance/insurance.module').then(
            (m) => m.InsuranceModule,
          ),
      },
      {
        path: 'email/subscription/:sid',
        // component: SubscriptionComponent,
        loadChildren: () =>
          import('./features/general/subscription/subscription.module').then(
            (m) => m.SubscriptionModule,
          ),
      },
      {
        path: 'email/unsubscribe/:sid',
        // component: UnsubscribeComponent,
        loadChildren: () =>
          import('./features/general/unsubscribe/unsubscribe.module').then(
            (m) => m.UnsubscribeModule,
          ),
      },
      {
        path: 'requestInfo',
        // component: RequestinfoComponent,
        loadChildren: () =>
          import('./features/requestinfo/requestinfo.module').then(
            (m) => m.RequestinfoModule,
          ),
      },
      {
        path: 'request-info/send',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import(
            './features/requestinfo/request-info-send/request-info-send.module'
          ).then((m) => m.RequestInfoSendModule),
      },

      {
        path: 'college/review',
        loadChildren: () =>
          import(
            './features/college/college-detail/college-review-v2/college-review-v2.component.module'
          ).then((m) => m.CollegeReviewV2ComponentModule),
      },
      {
        path: '',
        loadChildren: () =>
          import(
            './features/college/college-listing/college-listing.modules'
          ).then((m) => m.CollegeListingModule),
      },
      {
        path: ':college_alias',
        loadChildren: () =>
          import(
            './features/college/college-detail/college-detail.modules'
          ).then((m) => m.CollegeDetailModule),
      },
    ],
  },

  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
      scrollPositionRestoration: 'top',
      preloadingStrategy: SelectivePreloadingStrategy,
      enableTracing: false, // Set to true for debugging
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}